/**
 * ProsperMind Dashboard V2 - "Tyrant Mode"
 * Strict visual hierarchy, premium fintech feel
 * 
 * Layout Order (Non-negotiable):
 * 1. HERO - Stage + Progress (25-30% viewport, visually dominant)
 * 2. TODAY'S FOCUS - Action list (full width, prominent)
 * 3. CONTROL PANEL - 2x2 metrics grid (compact)
 * 4. MODULES - 4 grouped sections (secondary)
 * 5. INSIGHT STRIP - Horizontal scroll
 * 6. MINI PROGRESSION MAP - Thin bar
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Check, 
  TrendingUp, 
  TrendingDown,
  ArrowRight,
  Lock,
  Wallet,
  PiggyBank,
  Target,
  Activity,
  Sparkles,
  ChevronRight,
  BarChart3,
  Receipt,
  Calculator,
  BookOpen,
  Lightbulb,
  Brain
} from 'lucide-react';
import { Progress } from './ui/progress';

// Stage configuration
const STAGES = [
  { id: 'starter', name: 'Starter', nameEs: 'Inicial', color: '#6B7280' },
  { id: 'beginner', name: 'Beginner', nameEs: 'Principiante', color: '#F59E0B' },
  { id: 'active', name: 'Active', nameEs: 'Activo', color: '#10B981' },
  { id: 'progressing', name: 'Progressing', nameEs: 'Progresando', color: '#3B82F6' },
  { id: 'established', name: 'Established', nameEs: 'Establecido', color: '#8B5CF6' },
  { id: 'advanced', name: 'Advanced', nameEs: 'Avanzado', color: '#EC4899' },
  { id: 'mastery', name: 'Mastery', nameEs: 'Maestría', color: '#F59E0B' }
];

const DashboardV2 = ({ 
  userId, 
  onNavigate, 
  onOpenModule,
  canAccessFeature = () => true 
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // State
  const [journeyData, setJourneyData] = useState(null);
  const [todayActions, setTodayActions] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [insights, setInsights] = useState([]);
  const [heroInsight, setHeroInsight] = useState(null);
  const [moduleVisibility, setModuleVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [completingAction, setCompletingAction] = useState(null);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    if (!userId) return;
    
    try {
      // Fetch journey data (includes progressPercent from backend)
      const journeyRes = await fetch(`${API_URL}/api/gamification/journey/basic?user_id=${userId}`);
      if (journeyRes.ok) {
        const data = await journeyRes.json();
        setJourneyData(data);
      }
      
      // Fetch next action from backend (single source of truth)
      const actionsRes = await fetch(`${API_URL}/api/gamification/next-action?user_id=${userId}`);
      if (actionsRes.ok) {
        const data = await actionsRes.json();
        if (data.action) {
          setTodayActions([data.action]);
        } else {
          setTodayActions([]);
        }
      }
      
      // Fetch module visibility from backend
      const visibilityRes = await fetch(`${API_URL}/api/gamification/modules/visibility?user_id=${userId}`);
      if (visibilityRes.ok) {
        const data = await visibilityRes.json();
        setModuleVisibility(data.modules || {});
      }
      
      // Fetch user metrics
      const metricsRes = await fetch(`${API_URL}/api/my-numbers/${userId}`);
      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
      }
      
      // Fetch dynamic insights from Insight Engine
      const insightsRes = await fetch(`${API_URL}/api/insights/${userId}?lang=${lang}&max_insights=3`);
      if (insightsRes.ok) {
        const data = await insightsRes.json();
        if (data.insights) {
          setInsights(data.insights);
        }
      }
      
      // Fetch hero insight
      const heroInsightRes = await fetch(`${API_URL}/api/insights/hero/${userId}?lang=${lang}`);
      if (heroInsightRes.ok) {
        const data = await heroInsightRes.json();
        setHeroInsight(data);
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, API_URL, lang]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Listen for events from other modules to refresh dashboard data
  useEffect(() => {
    const handleDataUpdate = () => {
      // Refresh all data from backend after any module update
      fetchDashboardData();
    };

    // Listen for My Numbers save
    window.addEventListener('myNumbersUpdated', handleDataUpdate);
    // Listen for expense tracked
    window.addEventListener('expenseTracked', handleDataUpdate);
    // Listen for quiz completed
    window.addEventListener('quizCompleted', handleDataUpdate);
    // Generic data refresh event
    window.addEventListener('refreshDashboard', handleDataUpdate);

    return () => {
      window.removeEventListener('myNumbersUpdated', handleDataUpdate);
      window.removeEventListener('expenseTracked', handleDataUpdate);
      window.removeEventListener('quizCompleted', handleDataUpdate);
      window.removeEventListener('refreshDashboard', handleDataUpdate);
    };
  }, [fetchDashboardData]);

  // Handle action completion
  const handleActionComplete = async (action) => {
    if (completingAction === action.id) return;
    setCompletingAction(action.id);
    
    try {
      // Fire gamification event
      await fetch(`${API_URL}/api/gamification/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventType: action.actionType || 'taskCompleted',
          userId,
          data: { actionId: action.id }
        })
      });
      
      // Mark as completed locally
      setTodayActions(prev => 
        prev.map(a => a.id === action.id ? { ...a, completed: true } : a)
      );
      
      // Refresh data after animation
      setTimeout(() => {
        fetchDashboardData();
        setCompletingAction(null);
      }, 800);
      
    } catch (error) {
      console.error('Error completing action:', error);
      setCompletingAction(null);
    }
  };

  // Get current stage info
  const currentStageIndex = STAGES.findIndex(s => s.id === journeyData?.journeyStage);
  const safeCurrentIndex = currentStageIndex >= 0 ? currentStageIndex : 0;
  const currentStage = STAGES[safeCurrentIndex] || STAGES[0];
  const nextStage = STAGES[safeCurrentIndex + 1] || STAGES[1]; // Fallback to Beginner if no next stage
  const progress = journeyData?.progressPercent || 0;

  // Get module status from backend visibility data
  const getModuleStatus = (moduleKey) => {
    const visibility = moduleVisibility[moduleKey];
    if (!visibility) return 'active'; // Default to active if not in visibility data
    if (visibility.status === 'visible') return 'active';
    if (visibility.status === 'reduced') return 'locked';
    return 'locked';
  };

  // Check if module is accessible (from backend visibility)
  const isModuleAccessible = (moduleKey) => {
    const visibility = moduleVisibility[moduleKey];
    return !visibility || visibility.status === 'visible';
  };

  // Calculate metrics
  const cashFlow = metrics?.income ? metrics.income - (metrics.fixed_expenses || 0) - (metrics.variable_expenses || 0) : 0;
  const netWorth = metrics?.total_assets ? metrics.total_assets - (metrics?.total_liabilities || 0) : 0;
  const freedomKPI = metrics?.income && metrics?.passive_income ? Math.round((metrics.passive_income / metrics.income) * 100) : 0;
  const consistency = journeyData?.streak || 0;

  // Progress bar animation state
  const [animatedProgress, setAnimatedProgress] = useState(0);
  
  useEffect(() => {
    // Animate progress bar on load
    const timer = setTimeout(() => {
      setAnimatedProgress(progress);
    }, 100);
    return () => clearTimeout(timer);
  }, [progress]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Get insight message based on progress
  const getInsightMessage = () => {
    if (progress < 20) {
      return lang === 'es' ? 'Cada acción cuenta. Sigue así.' : 'Every action counts. Keep going.';
    } else if (progress < 50) {
      return lang === 'es' ? 'Buen progreso. Estás en camino.' : 'Good progress. You\'re on track.';
    } else if (progress < 80) {
      return lang === 'es' ? 'Excelente avance. Casi llegas.' : 'Excellent progress. Almost there.';
    } else {
      return lang === 'es' ? 'Increíble. El siguiente nivel te espera.' : 'Amazing. Next level awaits.';
    }
  };

  // Fix 4: Get stage motivational context (under 8 words)
  const getStageMotivation = () => {
    const stage = journeyData?.journeyStage || 'starter';
    const motivations = {
      starter: {
        en: "Taking your first financial steps.",
        es: "Dando tus primeros pasos financieros."
      },
      beginner: {
        en: "Building your tracking habit.",
        es: "Construyendo tu hábito de seguimiento."
      },
      active: {
        en: "Consistency is becoming your strength.",
        es: "La consistencia es tu fortaleza."
      },
      progressing: {
        en: "Your financial awareness is growing.",
        es: "Tu conciencia financiera crece."
      },
      established: {
        en: "Solid foundation, ready to optimize.",
        es: "Base sólida, listo para optimizar."
      },
      advanced: {
        en: "Mastering your financial journey.",
        es: "Dominando tu viaje financiero."
      },
      expert: {
        en: "Financial freedom within reach.",
        es: "Libertad financiera al alcance."
      }
    };
    return motivations[stage]?.[lang] || motivations.starter[lang];
  };

  // Fix 2: Get requirement visual state
  const getRequirementState = (req) => {
    if (req.completed) return 'completed';
    if (req.current > 0 && req.current < req.target) return 'in-progress';
    return 'not-started';
  };

  return (
    <div className="space-y-4 pb-8">
      {/* ========== 1. HERO SECTION - Mobile-First Premium Design ========== */}
      <section 
        className="relative w-full overflow-hidden rounded-3xl"
        style={{ minHeight: '280px' }}
        data-testid="hero-section"
      >
        {/* Premium dark gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800" />
        
        {/* Subtle warm accent glow behind percentage area */}
        <div className="absolute top-1/2 right-0 w-48 h-48 md:w-72 md:h-72 bg-amber-500/15 rounded-full blur-3xl transform translate-x-1/4 -translate-y-1/2" />
        <div className="absolute bottom-0 left-1/4 w-40 h-40 bg-emerald-500/8 rounded-full blur-2xl" />
        
        {/* Content Container - Mobile-first stacked layout */}
        <div className="relative z-10 p-6 md:p-8 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-10">
          
          {/* === LEFT SIDE (Mobile: Top, Desktop: 55%) === */}
          <div className="md:w-[55%] space-y-5">
            
            {/* Stage Header - Title block is FIRST thing user understands */}
            <div className="flex items-center gap-4">
              {/* Stage Icon - reinforces identity, doesn't dominate */}
              <div 
                className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center shadow-xl ring-2 ring-white/10"
                style={{ backgroundColor: currentStage.color }}
              >
                <Target className="w-6 h-6 md:w-8 md:h-8 text-white" />
              </div>
              
              {/* Stage Title & Subtitle */}
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-none">
                  {lang === 'es' ? currentStage.nameEs : currentStage.name}
                </h1>
                <p className="text-gray-400 text-sm md:text-base mt-1">
                  {lang === 'es' ? 'Tu etapa actual' : 'Your current stage'}
                </p>
                {/* Fix 4: Stage motivational context line */}
                <p className="text-amber-400/80 text-xs md:text-sm mt-1 font-medium">
                  {getStageMotivation()}
                </p>
              </div>
            </div>
            
            {/* Progress Bar - Critical visual element, THICK and important */}
            <div className="space-y-3">
              {/* Progress bar with end label (Fix 3) */}
              <div className="relative">
                <div className="h-4 md:h-5 lg:h-6 bg-gray-700/60 rounded-full overflow-hidden shadow-inner ring-1 ring-white/5">
                  <div 
                    className="h-full rounded-full transition-all ease-out shadow-lg"
                    style={{ 
                      width: `${animatedProgress}%`,
                      background: 'linear-gradient(90deg, #F59E0B 0%, #10B981 100%)',
                      transitionDuration: '1200ms',
                      boxShadow: '0 0 20px rgba(245, 158, 11, 0.4)'
                    }}
                  />
                </div>
                {/* Fix 3: Progress bar end label */}
                <div className="absolute right-0 -top-5 text-xs text-gray-500">
                  100% → <span className="text-emerald-400 font-medium">{lang === 'es' ? nextStage?.nameEs : nextStage?.name}</span>
                </div>
              </div>
              
              {/* Fix 2: Progress checklist with 3 DISTINCT visual states */}
              <div className="space-y-2 mt-4">
                {journeyData?.stageRequirements?.slice(0, 2).map((req, i) => {
                  const state = getRequirementState(req);
                  return (
                    <div key={i} className="flex items-center gap-2.5 text-sm md:text-base">
                      {/* Visual state indicator - 3 distinct states */}
                      {state === 'completed' && (
                        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-sm shadow-emerald-500/50">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      {state === 'in-progress' && (
                        <div className="w-5 h-5 rounded-full flex-shrink-0 relative overflow-hidden border-2 border-amber-500">
                          {/* Half-filled effect */}
                          <div 
                            className="absolute bottom-0 left-0 right-0 bg-amber-500"
                            style={{ height: `${(req.current / req.target) * 100}%` }}
                          />
                        </div>
                      )}
                      {state === 'not-started' && (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex-shrink-0" />
                      )}
                      
                      {/* Text with appropriate styling */}
                      <span className={`
                        ${state === 'completed' ? 'text-gray-500 line-through' : ''}
                        ${state === 'in-progress' ? 'text-white' : ''}
                        ${state === 'not-started' ? 'text-gray-500' : ''}
                      `}>
                        {lang === 'es' ? req.text?.es : req.text?.en} ({req.current}/{req.target})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* === RIGHT SIDE (Mobile: Bottom, Desktop: 45%) === */}
          <div className="md:w-[45%] flex flex-row md:flex-col items-end md:items-end justify-between md:justify-center gap-3 md:gap-2 md:text-right">
            
            {/* Progress Percentage - THE visual anchor, MOST DOMINANT element */}
            <div className="flex items-baseline" data-testid="progress-percentage">
              <span className="text-6xl md:text-7xl lg:text-8xl font-black text-white tabular-nums tracking-tighter drop-shadow-lg">
                {progress}
              </span>
              <span className="text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-400 ml-1">%</span>
            </div>
            
            {/* Next Stage + Insight - Secondary info */}
            <div className="text-right">
              {/* Next Stage Label - ONLY this, no duplicate action text */}
              {nextStage && (
                <p className="text-base md:text-lg text-gray-400">
                  {lang === 'es' ? 'Siguiente:' : 'Next:'}{' '}
                  <span className="font-bold text-emerald-400">
                    {lang === 'es' ? nextStage.nameEs : nextStage.name}
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== FIX 1 & 5: NEXT ACTION HERO + VISUAL CONNECTION ========== */}
      {/* Combined section for actions */}
      <section className="space-y-4" data-testid="action-section">
        
        {/* NEXT ACTION HERO - Full width, visually dominant */}
        {todayActions.length > 0 && !todayActions[0]?.completed && (
          <div 
            className="relative overflow-hidden bg-gradient-to-r from-amber-500/20 via-orange-500/15 to-amber-500/10 border border-amber-500/40 rounded-2xl p-5 md:p-6 cursor-pointer group hover:border-amber-400 transition-all duration-300"
            onClick={() => {
              const action = todayActions[0];
              if (action.navigateTo) {
                // Check if this is an expense-related action
                const isExpenseAction = action.navigateTo === 'expenses' || 
                  action.action?.includes('expense') || 
                  action.action?.includes('log');
                onNavigate(action.navigateTo, isExpenseAction);
              } else {
                handleActionComplete(action);
              }
            }}
            data-testid="next-action-hero"
          >
            {/* Animated glow background */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            
            {/* Content */}
            <div className="relative z-10">
              {/* Label */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">
                  {lang === 'es' ? 'Próxima Acción' : 'Next Action'}
                </span>
              </div>
              
              {/* Main action text - Large and prominent */}
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">
                {lang === 'es' ? todayActions[0]?.message?.es : todayActions[0]?.message?.en}
              </h2>
              
              {/* CTA Button */}
              <button 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold px-6 py-3 rounded-xl transition-all duration-200 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 hover:scale-[1.02]"
                onClick={(e) => {
                  e.stopPropagation();
                  const action = todayActions[0];
                  if (action.navigateTo) {
                    // Check if this is an expense-related action
                    const isExpenseAction = action.navigateTo === 'expenses' || 
                      action.action?.includes('expense') || 
                      action.action?.includes('log');
                    onNavigate(action.navigateTo, isExpenseAction);
                  } else {
                    handleActionComplete(action);
                  }
                }}
              >
                <span>{lang === 'es' ? 'Comenzar' : 'Start Now'}</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              
              {/* Progress reward indicator */}
              <div className="absolute top-5 right-5 md:top-6 md:right-6">
                <span className="text-2xl md:text-3xl font-bold text-amber-400/80">
                  +{todayActions[0]?.reward || 10}%
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Today's Focus - Secondary tasks (if any beyond the hero) */}
        {todayActions.length > 1 && (
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-400 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400/60" />
                {lang === 'es' ? 'También pendiente' : 'Also Pending'}
              </h3>
              <span className="text-xs text-gray-500">
                {todayActions.filter(a => a.completed).length}/{todayActions.length}
              </span>
            </div>
            
            <div className="space-y-2">
              {todayActions.slice(1).map((action) => (
                <button
                  key={action.id}
                  onClick={() => !action.completed && (action.navigateTo ? onNavigate(action.navigateTo) : handleActionComplete(action))}
                  disabled={action.completed || completingAction === action.id}
                  className={`
                    w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200
                    ${action.completed 
                      ? 'bg-green-500/10 border border-green-500/20' 
                      : 'bg-gray-800/50 border border-gray-700 hover:border-amber-500/30'
                    }
                  `}
                >
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center
                    ${action.completed ? 'bg-green-500 border-green-500' : 'border-gray-500'}
                  `}>
                    {action.completed && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className={`flex-1 text-left text-sm ${action.completed ? 'text-gray-500 line-through' : 'text-gray-300'}`}>
                    {lang === 'es' ? action.message?.es : action.message?.en}
                  </span>
                  <span className={`text-xs font-medium ${action.completed ? 'text-green-400' : 'text-gray-500'}`}>
                    +{action.reward || 10}%
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* All actions completed state */}
        {todayActions.length > 0 && todayActions.every(a => a.completed) && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5 text-center">
            <Check className="w-12 h-12 text-emerald-400 mx-auto mb-2" />
            <h3 className="text-lg font-bold text-white mb-1">
              {lang === 'es' ? '¡Acciones completadas!' : 'Actions Complete!'}
            </h3>
            <p className="text-sm text-gray-400">
              {lang === 'es' ? 'Has completado todo por hoy. ¡Excelente!' : "You've completed everything for today. Excellent!"}
            </p>
          </div>
        )}
        
        {/* No actions state */}
        {todayActions.length === 0 && (
          <div className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-5 text-center">
            <p className="text-gray-400 text-sm">
              {lang === 'es' ? 'No hay acciones pendientes.' : 'No pending actions.'}
            </p>
          </div>
        )}
        
        {/* To-Do List Quick Access */}
        <button
          onClick={() => onNavigate('actionplan')}
          className="w-full flex items-center justify-between p-3 bg-gray-800/30 border border-gray-700/50 rounded-xl hover:border-purple-500/50 hover:bg-gray-800/50 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <Target className="w-4 h-4 text-purple-400" />
            </div>
            <span className="text-sm font-medium text-gray-300">
              {lang === 'es' ? 'Mi lista de pendientes' : 'My To-Do List'}
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </section>

      {/* ========== 3. CONTROL PANEL (2x2 grid, compact) - Fix 3: Empty states ========== */}
      <div className="flex flex-wrap gap-2">
        {/* Cash Flow - Fix 3: Empty state with context */}
        <div 
          className="flex-1 min-w-[45%] bg-gray-800/50 border border-gray-700 rounded-xl p-3 cursor-pointer hover:border-blue-500/30 transition-colors"
          onClick={() => cashFlow === 0 && onNavigate('mynumbers')}
        >
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <span className="text-xs text-gray-400">{lang === 'es' ? 'Flujo' : 'Cash Flow'}</span>
          </div>
          {cashFlow === 0 ? (
            <p className="text-xs text-gray-500 leading-snug">
              {lang === 'es' 
                ? 'Agrega ingresos y gastos' 
                : 'Add income & expenses'}
            </p>
          ) : (
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-white">
                ${Math.abs(cashFlow).toLocaleString()}
              </span>
              {cashFlow > 0 ? <TrendingUp className="w-3 h-3 text-green-400" /> : <TrendingDown className="w-3 h-3 text-red-400" />}
            </div>
          )}
        </div>
        
        {/* Net Worth - Fix 3: Empty state with context */}
        <div 
          className="flex-1 min-w-[45%] bg-gray-800/50 border border-gray-700 rounded-xl p-3 cursor-pointer hover:border-purple-500/30 transition-colors"
          onClick={() => netWorth === 0 && onNavigate('mynumbers')}
        >
          <div className="flex items-center gap-2 mb-1">
            <PiggyBank className="w-4 h-4 text-purple-400 flex-shrink-0" />
            <span className="text-xs text-gray-400">{lang === 'es' ? 'Patrimonio' : 'Net Worth'}</span>
          </div>
          {netWorth === 0 ? (
            <p className="text-xs text-gray-500 leading-snug">
              {lang === 'es' 
                ? 'Completa Mis Números' 
                : 'Complete My Numbers'}
            </p>
          ) : (
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-white">
                ${Math.abs(netWorth).toLocaleString()}
              </span>
              {netWorth > 0 ? <TrendingUp className="w-3 h-3 text-green-400" /> : <TrendingDown className="w-3 h-3 text-red-400" />}
            </div>
          )}
        </div>
        
        {/* Freedom KPI */}
        <div className="flex-1 min-w-[45%] bg-gradient-to-br from-amber-500/10 to-orange-500/5 border border-amber-500/30 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <span className="text-xs text-amber-400/80">{lang === 'es' ? 'Libertad' : 'Freedom'}</span>
          </div>
          {freedomKPI === 0 ? (
            <p className="text-xs text-gray-500 leading-snug">
              {lang === 'es' 
                ? 'Agrega ingresos pasivos' 
                : 'Add passive income'}
            </p>
          ) : (
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-bold text-white">{freedomKPI}</span>
              <span className="text-sm text-gray-400">%</span>
            </div>
          )}
        </div>
        
        {/* Consistency */}
        <div className="flex-1 min-w-[45%] bg-gray-800/50 border border-gray-700 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-green-400 flex-shrink-0" />
            <span className="text-xs text-gray-400">{lang === 'es' ? 'Racha' : 'Streak'}</span>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="text-lg font-bold text-white">{consistency}</span>
            <span className="text-sm text-gray-400">d</span>
          </div>
        </div>
      </div>

      {/* ========== 4. MODULES (Using backend visibility) ========== */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider px-1">
          {lang === 'es' ? 'Módulos' : 'Modules'}
        </h3>
        
        {/* Track Section */}
        <div className="space-y-2">
          <p className="text-xs text-gray-500 px-1">{lang === 'es' ? 'Registrar' : 'Track'}</p>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <ModuleCard
              icon={Receipt}
              label={lang === 'es' ? 'Gastos' : 'Expenses'}
              status={getModuleStatus('expenses')}
              onClick={() => isModuleAccessible('expenses') && onNavigate('expenses')}
            />
            <ModuleCard
              icon={Calculator}
              label={lang === 'es' ? 'Mis Números' : 'My Numbers'}
              status={getModuleStatus('mynumbers')}
              onClick={() => isModuleAccessible('mynumbers') && onNavigate('mynumbers')}
            />
          </div>
        </div>
        
        {/* Learn Section */}
        <div className="space-y-2">
          <p className="text-xs text-gray-500 px-1">{lang === 'es' ? 'Aprender' : 'Learn'}</p>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <ModuleCard
              icon={BarChart3}
              label={lang === 'es' ? 'Escalera' : 'Wealth Ladder'}
              status={getModuleStatus('wealthladder')}
              onClick={() => isModuleAccessible('wealthladder') && onOpenModule('wealthLadder')}
            />
            <ModuleCard
              icon={BookOpen}
              label={lang === 'es' ? 'Identidad' : 'Identity'}
              status={getModuleStatus('identity')}
              onClick={() => isModuleAccessible('identity') && onOpenModule('identity')}
            />
            {/* Understanding Expenses - Visible for all users */}
            <ModuleCard
              icon={Brain}
              label={lang === 'es' ? 'Entendiendo Gastos' : 'Understanding Expenses'}
              status="active"
              onClick={() => onOpenModule('understandingExpenses')}
            />
          </div>
        </div>
        
        {/* Plan Section */}
        <div className="space-y-2">
          <p className="text-xs text-gray-500 px-1">{lang === 'es' ? 'Planificar' : 'Plan'}</p>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <ModuleCard
              icon={Target}
              label={lang === 'es' ? 'Metas' : 'Goals'}
              status={getModuleStatus('goals')}
              onClick={() => isModuleAccessible('goals') && onNavigate('goals')}
            />
            <ModuleCard
              icon={Lightbulb}
              label={lang === 'es' ? 'Plan de Acción' : 'Action Plan'}
              status={getModuleStatus('actionplan')}
              onClick={() => isModuleAccessible('actionplan') && onNavigate('actionplan')}
            />
          </div>
        </div>
        
        {/* Analyze Section */}
        <div className="space-y-2">
          <p className="text-xs text-gray-500 px-1">{lang === 'es' ? 'Analizar' : 'Analyze'}</p>
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <ModuleCard
              icon={BarChart3}
              label={lang === 'es' ? 'Diagnóstico' : 'Diagnosis'}
              status={getModuleStatus('diagnosis')}
              onClick={() => isModuleAccessible('diagnosis') && onNavigate('diagnosis')}
            />
            <ModuleCard
              icon={TrendingUp}
              label={lang === 'es' ? 'Patrimonio' : 'Net Worth'}
              status={getModuleStatus('networth')}
              onClick={() => isModuleAccessible('networth') && onNavigate('networth')}
            />
          </div>
        </div>
      </section>

      {/* ========== 5. INSIGHT STRIP (Dynamic from Insight Engine) ========== */}
      <section className="overflow-x-auto scrollbar-hide" data-testid="insight-strip">
        <div className="flex gap-3 pb-2">
          {insights.length > 0 ? (
            insights.map((insight, index) => (
              <DynamicInsightCard 
                key={insight.id || index}
                insight={insight}
                onAction={onNavigate}
              />
            ))
          ) : (
            // Fallback static insights while loading
            <>
              <InsightCard 
                text={lang === 'es' 
                  ? 'El seguimiento diario aumenta el éxito financiero en un 42%' 
                  : 'Daily tracking increases financial success by 42%'
                }
              />
              <InsightCard 
                text={lang === 'es' 
                  ? 'Los usuarios que completan metas ahorran 3x más' 
                  : 'Users who complete goals save 3x more'
                }
              />
            </>
          )}
        </div>
      </section>

      {/* ========== 6. MINI PROGRESSION MAP ========== */}
      <section className="bg-gray-800/30 rounded-xl p-3">
        <div className="flex items-center justify-between gap-1">
          {STAGES.map((stage, i) => (
            <div key={stage.id} className="flex-1 flex items-center">
              <div 
                className={`
                  w-full h-1.5 rounded-full transition-all duration-500
                  ${i < currentStageIndex ? 'bg-gradient-to-r' : i === currentStageIndex ? 'bg-gradient-to-r' : 'bg-gray-700'}
                `}
                style={i <= currentStageIndex ? { 
                  background: `linear-gradient(90deg, ${STAGES[Math.max(0, i-1)]?.color || stage.color}, ${stage.color})`
                } : {}}
              />
              {i === currentStageIndex && (
                <div 
                  className="w-3 h-3 rounded-full border-2 border-white shadow-lg -ml-1"
                  style={{ backgroundColor: stage.color }}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2">
          <span className="text-xs text-gray-500">{lang === 'es' ? 'Inicio' : 'Start'}</span>
          <span className="text-xs text-amber-400 font-medium">
            {lang === 'es' ? currentStage.nameEs : currentStage.name}
          </span>
          <span className="text-xs text-gray-500">{lang === 'es' ? 'Maestría' : 'Mastery'}</span>
        </div>
      </section>
    </div>
  );
};

// Module Card Component
const ModuleCard = ({ icon: Icon, label, status, onClick }) => {
  const isLocked = status === 'locked';
  const isInProgress = status === 'in_progress';
  
  return (
    <button
      onClick={onClick}
      disabled={isLocked}
      className={`
        flex-shrink-0 flex flex-col items-center justify-center gap-2 
        w-24 h-20 rounded-xl transition-all duration-200
        ${isLocked 
          ? 'bg-gray-800/30 opacity-40 cursor-not-allowed' 
          : isInProgress
            ? 'bg-gray-800/50 border-2 border-amber-500/50 hover:border-amber-500'
            : 'bg-gray-800/50 border border-gray-700 hover:border-gray-500 hover:bg-gray-800'
        }
      `}
    >
      <div className="relative">
        <Icon className={`w-5 h-5 ${isLocked ? 'text-gray-600' : 'text-gray-300'}`} />
        {isLocked && (
          <Lock className="w-3 h-3 text-gray-500 absolute -top-1 -right-1" />
        )}
      </div>
      <span className={`text-xs ${isLocked ? 'text-gray-600' : 'text-gray-400'}`}>
        {label}
      </span>
    </button>
  );
};

// Insight Card Component (Static fallback)
const InsightCard = ({ text }) => (
  <div className="flex-shrink-0 bg-gray-800/30 border border-gray-700/50 rounded-lg px-4 py-2 max-w-[280px]">
    <p className="text-xs text-gray-400 leading-relaxed">{text}</p>
  </div>
);

// Dynamic Insight Card Component (From Insight Engine)
const DynamicInsightCard = ({ insight, onAction }) => {
  const typeColors = {
    action: 'border-amber-500/40 bg-amber-500/5',
    pattern: 'border-blue-500/40 bg-blue-500/5',
    opportunity: 'border-emerald-500/40 bg-emerald-500/5',
    progression: 'border-purple-500/40 bg-purple-500/5'
  };
  
  const typeTextColors = {
    action: 'text-amber-400',
    pattern: 'text-blue-400',
    opportunity: 'text-emerald-400',
    progression: 'text-purple-400'
  };
  
  const hasAction = insight.action && insight.action_label;
  
  return (
    <div 
      className={`
        flex-shrink-0 rounded-xl px-4 py-3 max-w-[300px] min-w-[260px]
        border transition-all duration-200
        ${typeColors[insight.type] || 'border-gray-700/50 bg-gray-800/30'}
        ${hasAction ? 'cursor-pointer hover:scale-[1.02] hover:shadow-lg' : ''}
      `}
      onClick={() => hasAction && onAction(insight.action)}
      data-testid={`insight-card-${insight.id}`}
    >
      {/* Main text */}
      <p className={`text-sm font-medium leading-snug ${typeTextColors[insight.type] || 'text-gray-300'}`}>
        {insight.text}
      </p>
      
      {/* Subtext */}
      {insight.subtext && (
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          {insight.subtext}
        </p>
      )}
      
      {/* Action button */}
      {hasAction && (
        <button 
          className={`
            mt-2 text-xs font-semibold px-3 py-1 rounded-full
            ${insight.type === 'action' ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : ''}
            ${insight.type === 'pattern' ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' : ''}
            ${insight.type === 'opportunity' ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : ''}
            ${insight.type === 'progression' ? 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30' : ''}
            transition-colors
          `}
          onClick={(e) => {
            e.stopPropagation();
            onAction(insight.action);
          }}
        >
          {insight.action_label} →
        </button>
      )}
    </div>
  );
};

export default DashboardV2;
